import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import routes from '../../config/settings/routes';
import {
  Header, HeaderContainer, HeaderName, HeaderGlobalBar, HeaderGlobalAction, SkipToContent, Tag, ActionableNotification,
} from '@carbon/react';
import { WarningFilled, UserAvatar } from '@carbon/icons-react';
import Typography from '../../components/Basics/Typography';
import LogoutModal from '../Authentication/Logout.container';
import NotificationHolder from '../../components/Layouts/NotificationHolder.container';
import { NotificationStatus } from '../../components/Layouts/Notification.container';
import { onLogOutThunk } from '../Authentication/Authentication.actions';
import { onRemoveNotificationThunk, onRemoveAllNotificationThunk } from './Navbar.actions';

/**
 * The Navbar's component.
 */
export const Navbar = ({ managementMenu = null }) => {
  const [open, setOpen] = useState(false);
  const button = useRef(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const { numberOfNotifications, notifications } = useSelector((state) => state.navbar);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [animationState, setAnimationState] = useState('');
  const [shouldRender, setShouldRender] = useState(false);

  const removeNotification = (notification) => {
    dispatch(onRemoveNotificationThunk(notification));
  };

  const handleLogout = () => {
    dispatch(
      onLogOutThunk({
        onCallback: () => history.push(routes.login),
      })
    );
  };

  const removeAllNotifications = () => {
    dispatch(onRemoveAllNotificationThunk());
  };

  const Title = () => (
    <>
      <Typography>Notificaciones</Typography>
      <Typography color="#f14405" onClick={removeAllNotifications}>
        Marcar Leídas
      </Typography>
    </>
  );

  const SubHeader = () => {
    const countByTitle = notifications.reduce((acc, item) => {
      if (item.status !== NotificationStatus.REMOVED) {
        acc[item.title] = (acc[item.title] || 0) + 1;
      }
      return acc;
    }, {});

    const isEmpty = Object.keys(countByTitle).length === 0;

    return (
      <>
        {isEmpty ? (
          <Typography>Sin notificaciones</Typography>
        ) : (
          Object.entries(countByTitle).map(([title, count]) => (
            <Typography className="cds--type-body-compact-01" key={title}>
              {`${title} (${count})`}
            </Typography>
          ))
        )}
      </>
    );
  };

  useEffect(() => {
    if (isPopupOpen) {
      setAnimationState('in');
      setShouldRender(true);
    } else {
      setAnimationState('out');
      const timeoutId = setTimeout(() => {
        setShouldRender(false);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [isPopupOpen]);

  const isSingleDigit = String(numberOfNotifications).length === 1;

  const tagStyle = isSingleDigit
    ? {
      width: '32px',
      height: '32px',
      padding: 0,
      borderRadius: '50%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }
    : {};

  return (
    <HeaderContainer
      render={({ isSideNavExpanded, onClickSideNavExpand }) => (
        <>
          <Header aria-label="Talabre">
            <SkipToContent />
            <HeaderName href="/" prefix="UCO 2.0">
              <Typography className="cds--type-heading-compact-01 header">TALABRE IX</Typography>
            </HeaderName>
            {managementMenu && managementMenu}
            <HeaderGlobalBar>
              <HeaderGlobalAction
                aria-label="Notificaciones"
                tooltipAlignment="end"
                onClick={() => setPopupOpen(!isPopupOpen)}
              >
                {numberOfNotifications > 0 && (
                  <WarningFilled size={20} style={{ marginRight: '0.5rem' }} />
                )}
                <Tag size="md" style={tagStyle}>
                  {numberOfNotifications}
                </Tag>
              </HeaderGlobalAction>
              <HeaderGlobalAction
                aria-label="Cerrar Sesión"
                tooltipAlignment="end"
                onClick={() => setOpen(true)}
              >
                <UserAvatar size={20} />
              </HeaderGlobalAction>
            </HeaderGlobalBar>
          </Header>
          <LogoutModal button={button.current} open={open} setOpen={setOpen} />
          {shouldRender && (
            <NotificationHolder
              animate={animationState}
              isOpen={isPopupOpen}
              onClose={() => setPopupOpen(false)}
              header={<Title />}
              subheader={<SubHeader />}
            >
              {notifications.length === 0 ? (
                ''
              ) : (
                notifications
                  .filter(
                    (notification) =>
                      notification.status === NotificationStatus.SHOW ||
                      notification.status === NotificationStatus.HIDDEN
                  )
                  .map((notification) => (
                    <ActionableNotification
                      key={notification.id}
                      role="status"
                      actionButtonLabel="Más información"
                      onActionButtonClick={() => history.push(notification.route)}
                      title={notification.title}
                      subtitle={
                        <span>
                          {notification.subtitle}
                          <br />
                          {notification.caption}
                        </span>
                      }
                      onClose={() => removeNotification(notification)}
                      kind={notification.kind}
                    />
                  ))
              )}
            </NotificationHolder>
          )}
        </>
      )}
    />
  );
};

Navbar.propTypes = {
  managementMenu: PropTypes.node,
};

export default Navbar;
